import { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Grid, TextField, Alert, MenuItem, Typography, Card, useTheme, Checkbox, FormControlLabel } from "@mui/material";
import { useAppSelector } from "@store/hooks";
import { selectAuth } from "@slices/authSlice";
import CountrySelectionHeader from "./Components/CountrySelectionHeader";
import CountryListItem from "./Components/CountryListItem";
import SelectDropdown from "@components/SelectDropdown";
import { ListData } from "@constants/listData";
import donorInfoContext from "@utilities/contexts/donorInfoContext";
import { addToStorage } from "@utilities/storageService";
import { pageRoutes } from "@constants/pageRoutes";
import { APICaller, useAPICaller } from "@api/request";
import { getUserDetails } from "@api/services/userService";
import { ErrorMessages } from "@constants/errorMessages";
import ShadowedButton from "@components/ShadowedButton";
import { sendEvent } from "@utilities/googleAnalytics";
import { GoogleAnalyticsEvents } from "@constants/googleAnalyticsEvents";
import { createOnChangeEvent } from "@utilities/customEventGenerator";

import PhoneInput from "react-phone-input-2";



const DonorInfoForm = ({
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    payment_type,
}: {
    values: {
        email: string;
        phone_number: string;
        name: string;
        address: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
        isAnnonymous: boolean;
        isAgreed: boolean;
        isOrganization: boolean;

        organizationName: string;
    };
    handleChange: any;
    handleBlur: any;
    errors: any;
    touched: any;
    payment_type: any;
}) => {
    const auth = useAppSelector(selectAuth);

    const navigate = useNavigate();
    const location = useLocation();
    const APICallerRef = useRef<APICaller>(useAPICaller());
    const theme = useTheme();

    const [profileDetail, setProfileDetail] = useState<{ label: string; value: any }[]>([]);

    const {
        setDonorName,
        setDonorPhoneNumber,
        setDonorEmail,
        setDonorAddress,
        setDonorCity,
        setDonorState,
        setDonorCountry,
        setDonorZipCode,
        setIsannonymous,
        setIsagreed,
        setIsOrganization,
        setOrganizationName,
    } = useContext(donorInfoContext);

    let countryDropdownContent: ReactNode[] = [];

    countryDropdownContent.push(<CountrySelectionHeader key={0} title={"Country"} />);
    countryDropdownContent.push(
        ListData.CountryList.map((item: { id: number; name: string }) => {
            return (
                <MenuItem key={item.id} value={item.name}>
                    <CountryListItem countryName={item.name} />
                </MenuItem>
            );
        })
    );

    useEffect(() => {
        setDonorName(values.name);
        setDonorEmail(values.email);
        setDonorPhoneNumber(values.phone_number);
        setDonorAddress(values.address);
        setDonorCity(values.city);
        setDonorState(values.state);
        setDonorCountry(values.country);
        setDonorZipCode(values.zipCode);
        setIsannonymous(values.isAnnonymous);
        setIsagreed(values.isAgreed);
        setIsOrganization(values.isOrganization);
        setOrganizationName(values.organizationName);
    }, [values, values.country]);
    useEffect(() => {
        if (auth.isLoggedIn) {
            getUserData();
        }
    }, [auth.isLoggedIn]);

    const handleLogin = () => {
        sendEvent(GoogleAnalyticsEvents.LoginButtonInDonationDialog);
        addToStorage("firstTimeSignupRedirectBackUrl", location.pathname);
        navigate(pageRoutes.LOGIN);
    };

    const getUserData = async () => {
        try {
            if (auth.userInfo?.id) {
                const { data: userDetails } = await getUserDetails(auth.userInfo?.id, APICallerRef.current);
            } else {
                throw new Error(ErrorMessages.NoLoggedInUserFound);
            }
        } catch (error: any) {
            console.error("Error fetching user data:", error);
        }
    };

    return (
        <Box marginY={0.5}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 2 }}>
                <svg
                    fill="none"
                    height="28"
                    viewBox="0 0 28 28"
                    width="28"
                    xmlns="http://www.w3.org/2000/svg"
                    className="icon-fill shrink-0 font-size-28 text-green-80 me-2"
                    aria-hidden="true"
                >
                    <g fill="#00c04b">
                        {" "}
                        {/* Set color here */}
                        <path
                            clipRule="evenodd"
                            d="m15.0393 2.44995c-.6707-.24772-1.4079-.24772-2.0786 0l-8.30715 3.06797c-.39274.14504-.65355.51939-.65355.93807v7.54411c0 3.6861 1.95965 6.6874 4.28911 8.8073 1.16017 1.0557 2.38789 1.8689 3.45309 2.4137 1.1081.5668 1.9145.779 2.2578.779s1.1497-.2122 2.2578-.779c1.0652-.5448 2.2929-1.358 3.4531-2.4137 2.3294-2.1199 4.2891-5.1212 4.2891-8.8073v-7.54411c0-.41868-.2608-.79303-.6536-.93807zm-2.7715-1.876139c1.1179-.412868 2.3465-.412868 3.4644 0l8.3071 3.067969c1.1783.43514 1.9607 1.55819 1.9607 2.81421v7.54411c0 4.4389-2.3618 7.9375-4.943 10.2865-1.2952 1.1786-2.6702 2.092-3.8885 2.7151-1.1754.6012-2.3332.9984-3.1685.9984s-1.9931-.3972-3.1685-.9984c-1.21831-.6231-2.59328-1.5365-3.88847-2.7151-2.58125-2.349-4.94303-5.8476-4.94303-10.2865v-7.54411c0-1.25602.78243-2.37907 1.96066-2.81421z"
                            fillRule="evenodd"
                        />
                        <path d="m18.2906 11.75h-.2535v-1.1855c0-2.19278-1.7415-4.02451-3.9182-4.06361-.0595-.00107-.1783-.00107-.2378 0-2.1767.0391-3.91819 1.87083-3.91819 4.06361v1.1855h-.25354c-.39069 0-.70937.4028-.70937.9003v5.9463c0 .4969.31868.9035.7094.9035h8.5812c.3907 0 .7094-.4066.7094-.9035v-5.9463c0-.4974-.3187-.9003-.7094-.9003zm-3.4867 3.8674v1.7967c0 .2058-.1723.3799-.3784.3799h-.8509c-.2061 0-.3785-.1741-.3785-.3799v-1.7967c-.1999-.1966-.3162-.4684-.3162-.7691 0-.5698.4408-1.0594 1.0013-1.082.0594-.0024.1783-.0024.2377 0 .5605.0226 1.0013.5122 1.0013 1.082 0 .3007-.1164.5725-.3163.7691zm1.5623-3.8674h-4.7323v-1.1855c0-1.30621 1.0623-2.38623 2.3661-2.38623s2.3662 1.08002 2.3662 2.38623z" />
                    </g>
                </svg>
                <Typography
                    sx={{
                        px: 1,
                    }}
                    variant="h3"
                >
                    Secure Donation 
                </Typography>
            </Box>
            {!auth.isLoggedIn && (
                <Card sx={{ border: "1px solid #E2E5EE", p: 2, mb: 2 }}>
                    <Grid container>
                        <Grid item xs={12} md={12}>
                            <Typography textAlign={"center"} fontSize={14} fontWeight={500} lineHeight={"22px"}>
                                Have an account with us?
                            </Typography>
                            {/* <Typography fontSize={14} lineHeight={"22px"} color={"#616D79"}>
                                Please login to your account
                            </Typography> */}
                            <ShadowedButton
                                sx={{
                                    backgroundColor: "#ce1337",
                                    mt: 1,
                                }}
                                fullWidth
                                variant="contained"
                                color="error"
                                onClick={handleLogin}
                            >
                                Login
                            </ShadowedButton>
                        </Grid>
                        {/* <Grid item xs={12} md={4} display={"flex"}
                         >
                    
                        </Grid> */}
                    </Grid>
                </Card>
            )}

            {auth.isLoggedIn && (
                <Box display={"flex"} flexDirection={"column"} marginY={1.5}>
                    <Grid item xs={12} display={"flex"} justifyContent={"flex-start"}>
                        <Alert severity="info">
                            You are currently logged in as <strong>[ {auth.userInfo?.username} ]</strong>
                        </Alert>
                    </Grid>
                </Box>
            )}

            <Box>
                <Card sx={{ border: "1px solid #E2E5EE", p: 2, mb: 2 }}>
                    <Box component="form">
                        <Box display={"flex"} marginY={1.5} gap={1}>
                            <TextField
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "4px",
                                        "& fieldset": {
                                            border: "1px solid #acacac",
                                        },
                                        "&:hover fieldset": {
                                            border: "1px solid #ce1337 !important",
                                            transition: "border 0.3s ease",
                                        },
                                        "&.Mui-focused fieldset": {
                                            border: "1px solid #ce1337",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#465a7e", // Label color
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#465a7e", // Label color when focused
                                    },
                                    "& .Mui-error.MuiFormHelperText-root": {
                                        fontWeight: "normal",
                                    },
                                }}
                                fullWidth
                                required
                                id="name"
                                label={"Your name"}
                                name="name"
                                autoComplete="Name"
                                value={values.name}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                helperText={errors.name && touched.name && errors.name}
                                error={Boolean(errors.name) && touched.name}
                            />
                            <TextField
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "4px",
                                        "& fieldset": {
                                            border: "1px solid #acacac",
                                        },
                                        "&:hover fieldset": {
                                            border: "1px solid #ce1337 !important",
                                            transition: "border 0.3s ease",
                                        },
                                        "&.Mui-focused fieldset": {
                                            border: "1px solid #ce1337",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#465a7e", // Label color
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#465a7e", // Label color when focused
                                    },
                                    "& .Mui-error.MuiFormHelperText-root": {
                                        fontWeight: "normal",
                                    },
                                }}
                                required
                                fullWidth
                                id="email"
                                label="Email address"
                                name="email"
                                autoComplete="Email"
                                value={values.email || ""}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                helperText={errors.email && touched.email && errors.email}
                                error={Boolean(errors.email) && touched.email}
                            />
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                marginY: 1.5,
                                position: "relative",
                                "& .phone-input-container": {
                                    width: "100%",
                                    boxSizing: "border-box", // Ensure padding and border are included in width
                                },
                                "& .phone-input-container .form-control": {
                                    width: "100%",
                                    height: "56px",
                                    borderRadius: "4px",
                                    border: "1px solid #acacac",
                                    fontSize: "13px",
                                    fontFamily: "inherit",
                                    paddingLeft: "45px", // Ensure consistent padding on the left for flag
                                    paddingRight: "10px", // Ensure padding on the right side as well
                                    boxSizing: "border-box", // Include padding and border in total width
                                },
                                "& .phone-input-container .form-control:focus": {
                                    border: "1px solid #ce1337 !important",
                                    boxShadow: "none",
                                },
                                "& .phone-input-container .form-control:hover": {
                                    border: "1px solid #ce1337 !important",
                                    transition: "border 0.3s ease",
                                },
                                "& .phone-input-container .error": {
                                    border: "1px solid #d32f2f !important",
                                },
                                "& .phone-input-container .flag-dropdown": {
                                    border: "1px solid #acacac",
                                    borderRight: "none",
                                    borderRadius: "4px 0 0 4px",
                                    backgroundColor: "white",
                                    zIndex: 20,
                                    boxSizing: "border-box", // Ensure consistent width
                                },
                                "& .phone-input-container .flag-dropdown:hover, & .phone-input-container .flag-dropdown.open": {
                                    border: "1px solid #ce1337 !important",
                                    borderRight: "none !important",
                                    transition: "border 0.3s ease",
                                },
                                "& .phone-input-container .selected-flag:focus": {
                                    border: "none !important",
                                    boxShadow: "none !important",
                                },
                                "& .phone-input-container:focus-within .flag-dropdown": {
                                    border: "1px solid #ce1337 !important",
                                    borderRight: "none !important",
                                },
                                "& .phone-input-container:focus-within .form-control": {
                                    border: "1px solid #ce1337 !important",
                                },
                                "& .special-label": {
                                    fontSize: "0.75rem",
                                    color: "#465a7e",
                                    top: "-0.75em",
                                    background: "white",
                                    padding: "0 5px",
                                    zIndex: 30,
                                    position: "absolute",
                                    left: "8px",
                                },
                                "& .country-list": {
                                    marginTop: "8px",
                                    zIndex: 20,
                                },
                                "& .react-tel-input .flag-dropdown.open .country-list": {
                                    top: "100%",
                                    marginTop: "10px",
                                },
                               
                            }}
                        >
                            <Typography
                                component="label"
                                sx={{
                                    fontSize: "0.75rem",
                                    color: "#465a7e",
                                    backgroundColor: "white",
                                    padding: "0 5px",
                                    position: "absolute",
                                    top: "-0.5em",
                                    left: "8px",
                                    zIndex: 30,
                                }}
                            >
                                Phone number{payment_type !== "Bkash" ? " *" : ""}
                            </Typography>

                            <PhoneInput
                                country="bd"
                                value={values.phone_number}
                                onChange={(phone: string) =>
                                    handleChange({
                                        target: {
                                            name: "phone_number",
                                            value: `+${phone}`,
                                        },
                                    })
                                }
                                specialLabel=""
                                inputClass={errors.phone_number && touched.phone_number ? "error" : ""}
                                containerClass="phone-input-container"
                                enableSearch={true}
                                dropdownClass="phone-dropdown"
                            />
                            {errors.phone_number && touched.phone_number && (
                                <Typography
                                    sx={{
                                        color: "error.main",
                                        marginLeft: "6px",
                                        fontSize: "0.75rem",
                                        fontWeight: "normal",
                                    }}
                                >
                                    {errors.phone_number}
                                </Typography>
                            )}
                        </Box>

                        <Box display={"flex"} marginY={1.5}>
                            <TextField
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "4px",
                                        "& fieldset": {
                                            border: "1px solid #acacac",
                                        },
                                        "&:hover fieldset": {
                                            border: "1px solid #ce1337 !important",
                                            transition: "border 0.3s ease",
                                        },
                                        "&.Mui-focused fieldset": {
                                            border: "1px solid #ce1337",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#465a7e", // Label color
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#465a7e", // Label color when focused
                                    },
                                    "& .Mui-error.MuiFormHelperText-root": {
                                        fontWeight: "normal",
                                    },
                                }}
                                fullWidth
                                id="address"
                                label={`Address${payment_type !== "Bkash" && payment_type !== "Nagad" ? " *" : ""}`}
                                name="address"
                                autoComplete="Address"
                                value={values.address}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                helperText={errors.address && touched.address && errors.address}
                                error={Boolean(errors.address) && touched.address}
                            />
                        </Box>
                        <Box display={"flex"} marginY={1.5} gap={1}>
                            <TextField
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "4px",
                                        "& fieldset": {
                                            border: "1px solid #acacac",
                                        },
                                        "&:hover fieldset": {
                                            border: "1px solid #ce1337 !important",
                                            transition: "border 0.3s ease",
                                        },
                                        "&.Mui-focused fieldset": {
                                            border: "1px solid #ce1337",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#465a7e", // Label color
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#465a7e", // Label color when focused
                                    },
                                    "& .Mui-error.MuiFormHelperText-root": {
                                        fontWeight: "normal",
                                    },
                                }}
                                fullWidth
                                id="city"
                                label={`District/City${payment_type !== "Bkash" && payment_type !== "Nagad" ? " *" : ""}`}
                                name="city"
                                autoComplete="City"
                                value={values.city}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                helperText={errors.city && touched.city && errors.city}
                                error={Boolean(errors.city) && touched.city}
                                // sx={{ mr: 1 }}
                            />
                            <TextField
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "4px",
                                        "& fieldset": {
                                            border: "1px solid #acacac",
                                        },
                                        "&:hover fieldset": {
                                            border: "1px solid #ce1337 !important",
                                            transition: "border 0.3s ease",
                                        },
                                        "&.Mui-focused fieldset": {
                                            border: "1px solid #ce1337",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#465a7e", // Label color
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#465a7e", // Label color when focused
                                    },
                                    "& .Mui-error.MuiFormHelperText-root": {
                                        fontWeight: "normal",
                                    },
                                }}
                                fullWidth
                                id="state"
                                label="State"
                                name="state"
                                autoComplete="State"
                                value={values.state}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                helperText={errors.state && touched.state && errors.state}
                                error={Boolean(errors.state) && touched.state}
                                // sx={{ ml: 1 }}
                            />
                        </Box>
                        <Box
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "4px",
                                    "& fieldset": {
                                        border: "1px solid #acacac",
                                    },
                                    "&:hover fieldset": {
                                        border: "1px solid #ce1337 !important",
                                        transition: "border 0.3s ease",
                                    },
                                    "&.Mui-focused fieldset": {
                                        border: "1px solid #ce1337",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "#465a7e", // Label color
                                },
                                "& .MuiInputLabel-root.Mui-focused": {
                                    color: "#465a7e", // Label color when focused
                                },
                                "& .Mui-error.MuiFormHelperText-root": {
                                    fontWeight: "normal",
                                },
                            }}
                            display={"flex"}
                            marginY={1.5}
                            gap={1}
                        >
                            <TextField
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "4px",
                                        "& fieldset": {
                                            border: "1px solid #acacac",
                                        },
                                        "&:hover fieldset": {
                                            border: "1px solid #ce1337 !important",
                                            transition: "border 0.3s ease",
                                        },
                                        "&.Mui-focused fieldset": {
                                            border: "1px solid #ce1337",
                                        },
                                    },
                                    "& .MuiInputLabel-root": {
                                        color: "#465a7e", // Label color
                                    },
                                    "& .MuiInputLabel-root.Mui-focused": {
                                        color: "#465a7e", // Label color when focused
                                    },
                                    "& .Mui-error.MuiFormHelperText-root": {
                                        fontWeight: "normal",
                                    },
                                }}
                                fullWidth
                                id="zipCode"
                                label="Post code"
                                name="zipCode"
                                autoComplete="Zip Code"
                                value={values.zipCode}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                helperText={errors.zipCode && touched.zipCode && errors.zipCode}
                                error={Boolean(errors.zipCode) && touched.zipCode}
                            />
                            <SelectDropdown
                                label={"Country"}
                                dropdownContent={countryDropdownContent}
                                value={values.country} // Set default here
                                setValue={(selectedCountry) => {
                                    values.country = selectedCountry;
                                    handleChange(createOnChangeEvent("country", selectedCountry));
                                }}
                                error={Boolean(errors.country) && touched.country} // ✅ Pass error state
                                helperText={errors.country && touched.country ? errors.country : ""} //
                            />
                        </Box>
                        <Box display="flex" gap={2} marginTop={1.5}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.isAnnonymous}
                                        onChange={handleChange}
                                        name="isAnnonymous"
                                        sx={{
                                            color: "#ce1337",
                                            "&.Mui-checked": { color: "#ce1337" },
                                        }}
                                    />
                                }
                                label="Donate annonymously"
                                sx={{ color: "#465a7e" }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={values.isOrganization}
                                        onChange={handleChange}
                                        name="isOrganization"
                                        sx={{
                                            color: "#ce1337",
                                            "&.Mui-checked": { color: "#ce1337" },
                                        }}
                                    />
                                }
                                label="Donate as an organization"
                                sx={{ color: "#465a7e" }}
                            />
                        </Box>
                        {values.isOrganization && (
                            <Box display={"flex"} marginY={1.5}>
                                <TextField
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "4px",
                                            "& fieldset": {
                                                border: "1px solid #acacac",
                                            },
                                            "&:hover fieldset": {
                                                border: "1px solid #ce1337 !important",
                                                transition: "border 0.3s ease",
                                            },
                                            "&.Mui-focused fieldset": {
                                                border: "1px solid #ce1337",
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: "#465a7e", // Label color
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                            color: "#465a7e", // Label color when focused
                                        },
                                        "& .Mui-error.MuiFormHelperText-root": {
                                            fontWeight: "normal",
                                        },
                                    }}
                                    fullWidth
                                    required
                                    id="organizationName"
                                    label="Organization name"
                                    name="organizationName"
                                    autoComplete="organizationName"
                                    value={values.organizationName}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    helperText={errors.organizationName && touched.organizationName && errors.organizationName}
                                    error={Boolean(errors.organizationName) && touched.organizationName}
                                />
                            </Box>
                        )}
                    </Box>
                </Card>

                {/* <Typography sx={{ textAlign: "center", fontSize: "14px" }}> */}
                <Box sx={{ marginLeft: "20px" }}> 
    <FormControlLabel
        control={
            <Checkbox
                checked={values.isAgreed}
                onChange={handleChange}
                name="isOrganization"
                sx={{
                    color: "#ce1337",
                    "&.Mui-checked": { color: "#ce1337" },
                }}
            />
        }
        label="I agree with terms and conditions, privacy and refund policy"
        sx={{ color: "#465a7e", marginRight: "8px" }} // Adds some space between checkbox and label
    />

<Typography
    fontSize={14}
    sx={{
        color: "#465a7e",
        marginTop: 0,
        marginLeft: "30px",  // Adds 25px left margin
        marginRight: "30px", // Adds 25px right margin
        textAlign: "left", // Centers the text within the available space
        width: "calc(100% - 50px)", // Adjusts width to account for both left and right margins
        lineHeight: "1.5", // Adds a bit of spacing for readability
    }}
>
    You will be redirected to our payment partner when you proceed with the payment.
</Typography>


</Box>

            </Box>
            {/* )} */}
        </Box>
    );
};

export default DonorInfoForm;
